import React, { useId, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowDown, ArrowUp, ChevronLeft, Image as ImageIcon, Plus, Trash2, Video } from "lucide-react";

import { uploadFile } from "@/lib/api";
import { Button, IconCircle } from "@/components/kit";

export const OPTS = {
  hair_types: ["straight", "wavy", "curly", "coily"],
  lengths: ["buzz", "short", "medium", "long"],
  difficulty: ["easy", "medium", "advanced"],
  maintenance: ["low", "medium", "high"],
  hold: ["light", "medium", "strong"],
  finish: ["matte", "natural"],
  faces: ["oval", "round", "square", "oblong", "heart", "diamond", "triangle"],
  categories: ["Straight", "Wavy", "Curly", "Coily", "Short", "Medium", "Long", "Low-Maintenance", "Trending", "Barber-Inspired"],
  tags: ["barber-inspired", "classic", "curly-natural", "fade", "flow", "low-maintenance", "short-neat", "textured", "trending"],
  families: ["buzz", "coil", "crop", "curl", "curl-long", "fade", "flow", "flow-wave", "hightop", "kstyle", "locs", "mullet", "part", "quiff", "sidepart", "slick", "sponge", "twist"],
  products: ["matte-clay", "matte-styling-cream", "curl-cream", "leave-in-conditioner"],
};

/** Admin page shell: back button + kicker + title, wide content column. */
export function AdminScreen({ title, testId, children, right, kicker = "BREEZ Admin", fallback = "/admin" }) {
  const navigate = useNavigate();
  return (
    <div data-testid={testId} className="min-h-screen bg-white">
      <div className="border-b border-divider bg-white px-4 pb-3 pt-4 md:px-6">
        <div className="mx-auto flex w-full max-w-[1100px] items-center gap-3">
          <IconCircle testId={`${testId}-back`} icon={ChevronLeft} label="Back" onClick={() => (window.history.length > 1 ? navigate(-1) : navigate(fallback, { replace: true }))} />
          <div className="min-w-0 flex-1">
            <p className="t-label">{kicker}</p>
            <h1 className="t-h2 truncate">{title}</h1>
          </div>
          {right}
        </div>
      </div>
      <div className="px-4 pb-16 pt-5 md:px-6">
        <div className="mx-auto flex w-full max-w-[1100px] flex-col gap-5">{children}</div>
      </div>
    </div>
  );
}

export function Section({ title, children, testId, note, className = "" }) {
  return (
    <section data-testid={testId} className={`rounded-[20px] border border-line bg-off p-4 md:p-5 ${className}`}>
      <h2 className="t-title">{title}</h2>
      {note ? <p className="t-caption mt-0.5">{note}</p> : null}
      <div className="mt-3 flex flex-col gap-3">{children}</div>
    </section>
  );
}

export function Field({ label, value, onChange, testId, multiline, placeholder, numeric, type = "text" }) {
  const id = useId();
  const cls = "field-input !bg-white !border-[#D1D5DB]";
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="t-label">{label}</label>
      {multiline ? (
        <textarea id={id} data-testid={testId} value={value ?? ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} rows={4} className={`${cls} min-h-[96px] resize-y`} />
      ) : (
        <input id={id} data-testid={testId} type={type} inputMode={numeric ? "numeric" : undefined} value={value ?? ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} autoCapitalize={type === "password" || /email/i.test(label) ? "none" : "sentences"} className={cls} />
      )}
    </div>
  );
}

export function Chips({ label, options, value, onChange, testId, single }) {
  const v = value ?? [];
  return (
    <fieldset className="flex flex-col gap-1.5">
      <legend className="t-label mb-1.5">{label}</legend>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => {
          const on = v.includes(o);
          return (
            <button
              key={o}
              type="button"
              aria-pressed={on}
              data-testid={`${testId}-${o}`}
              onClick={() => onChange(single ? [o] : on ? v.filter((x) => x !== o) : [...v, o])}
              className={`focus-ring min-h-[44px] rounded-full border px-4 font-body-semi text-xs transition-colors ${on ? "border-navy bg-navy text-white" : "border-[#D1D5DB] bg-white text-ink hover:border-ocean"}`}
            >
              {o}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}

export function Toggle({ label, value, onChange, testId }) {
  return (
    <div className="flex min-h-[44px] items-center justify-between gap-3">
      <span className="t-strong flex-1">{label}</span>
      <button
        type="button"
        role="switch"
        aria-checked={!!value}
        aria-label={label}
        data-testid={testId}
        onClick={() => onChange(!value)}
        className={`focus-ring relative h-7 w-12 shrink-0 rounded-full transition-colors ${value ? "bg-ocean" : "bg-[#D1D5DB]"}`}
      >
        <span className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition-all ${value ? "left-[22px]" : "left-0.5"}`} />
      </button>
    </div>
  );
}

function SmallIcon({ icon: Icon, onClick, testId, label, danger }) {
  return (
    <button type="button" data-testid={testId} aria-label={label} onClick={onClick} className="focus-ring flex h-11 w-11 items-center justify-center rounded-full bg-tert transition-colors hover:bg-sky">
      <Icon className={`h-4 w-4 ${danger ? "text-[#EF4444]" : "text-navy"}`} aria-hidden />
    </button>
  );
}

/** Ordered list editor: edit, reorder, delete, add. */
export function ListEditor({ label, items, onChange, testId, numbered }) {
  const list = items ?? [];
  const move = (i, d) => {
    const n = [...list];
    const j = i + d;
    if (j < 0 || j >= n.length) return;
    [n[i], n[j]] = [n[j], n[i]];
    onChange(n);
  };
  return (
    <div className="flex flex-col gap-2">
      <span className="t-label">{label}</span>
      {list.map((it, i) => (
        <div key={i} className="flex flex-col gap-1.5 border-b border-divider pb-2">
          <div className="flex items-start gap-2">
            {numbered ? <span className="t-strong mt-2.5 w-6">{i + 1}.</span> : null}
            <textarea aria-label={`${label} ${i + 1}`} data-testid={`${testId}-${i}`} value={it} rows={Math.min(6, 1 + Math.floor(it.length / 60))} onChange={(e) => onChange(list.map((x, k) => (k === i ? e.target.value : x)))} className="field-input !bg-white !border-[#D1D5DB] flex-1 resize-y" />
          </div>
          <div className="flex justify-end gap-1.5">
            <SmallIcon testId={`${testId}-up-${i}`} icon={ArrowUp} label="Move up" onClick={() => move(i, -1)} />
            <SmallIcon testId={`${testId}-down-${i}`} icon={ArrowDown} label="Move down" onClick={() => move(i, 1)} />
            <SmallIcon testId={`${testId}-del-${i}`} icon={Trash2} label="Delete" danger onClick={() => onChange(list.filter((_, k) => k !== i))} />
          </div>
        </div>
      ))}
      <Button testId={`${testId}-add`} title="Add" icon={Plus} variant="outline" onClick={() => onChange([...list, ""])} className="self-start !min-h-[44px]" />
    </div>
  );
}

const TYPES = { image: "image/jpeg,image/png,image/webp", video: "video/mp4,video/quicktime" };

export function UploadButton({ kind, title, testId, onUploaded, onError, path }) {
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    try {
      onUploaded(await uploadFile(f, kind, path));
    } catch (err) {
      onError(err.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <input ref={ref} type="file" accept={TYPES[kind]} className="hidden" data-testid={`${testId}-input`} onChange={onFile} />
      <Button testId={testId} title={busy ? "Uploading…" : title} icon={kind === "image" ? ImageIcon : Video} variant="outline" loading={busy} onClick={() => ref.current?.click()} className="self-start !min-h-[44px]" />
    </>
  );
}
